/**
 * Global Modal Service
 * Manages modals that need to appear across pages during background tasks
 */

import type { IDeletedProjectCheckResponse } from './businessCaseService';

type ModalType = 
  | 'deleted-project-warning'
  | 'project-name-mismatch'
  | 'validation-error'
  | 'existing-project-warning'
  | null;

interface DeletedProjectModalData {
  projectName: string;
  deletedAt: string;
  deletedByName: string;
  bcId: number;
  fileName: string;
  taskId: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

interface ProjectNameMismatchModalData {
  originalProjectName: string;
  fileProjectName: string;
  taskId: string;
  onChooseOriginal: () => Promise<void>;
  onChooseNew: () => Promise<void>;
  onCancel: () => void;
}

interface ValidationErrorModalData {
  errorType: string;
  errorMessage: string;
  taskId: string;
  onClose: () => void;
}

interface ExistingProjectWarningModalData {
  projectName: string;
  bcName: string;
  gpm: string;
  bcId: number;
  taskId: string;
  onCancel: () => void;
}

type ModalData = DeletedProjectModalData | ProjectNameMismatchModalData | ValidationErrorModalData | ExistingProjectWarningModalData | null;

interface GlobalModalState {
  type: ModalType;
  data: ModalData;
  isOpen: boolean;
}

type ModalListener = (state: GlobalModalState) => void;

class GlobalModalManager {
  private state: GlobalModalState = {
    type: null,
    data: null,
    isOpen: false,
  };

  private listeners: Set<ModalListener> = new Set();

  /**
   * Subscribe to modal state changes
   */
  subscribe(listener: ModalListener): () => void {
    this.listeners.add(listener);

    // Immediately notify with current state
    listener(this.state);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.state);
      } catch (error) {
        console.error('Error in modal listener:', error);
      }
    });
  }

  /**
   * Show deleted project warning modal
   */
  showDeletedProjectWarning(data: DeletedProjectModalData) {
    this.state = {
      type: 'deleted-project-warning',
      data,
      isOpen: true,
    };
    this.notifyListeners();
  }

  /**
   * Show project name mismatch modal
   */
  showProjectNameMismatch(data: ProjectNameMismatchModalData) {
    this.state = {
      type: 'project-name-mismatch',
      data,
      isOpen: true,
    };
    this.notifyListeners();
  }

  /**
   * Show validation error modal
   */
  showValidationError(data: ValidationErrorModalData) {
    this.state = {
      type: 'validation-error',
      data,
      isOpen: true,
    };
    this.notifyListeners();
  }

  /**
   * Show existing project warning modal
   */
  showExistingProjectWarning(data: ExistingProjectWarningModalData) {
    this.state = {
      type: 'existing-project-warning',
      data,
      isOpen: true,
    };
    this.notifyListeners();
  }

  /**
   * Close current modal
   */
  closeModal() {
    this.state = {
      type: null,
      data: null,
      isOpen: false,
    };
    this.notifyListeners();
  }

  /**
   * Get current modal state
   */
  getState(): GlobalModalState {
    return { ...this.state };
  }

  /**
   * Check if a modal is currently open
   */
  isModalOpen(): boolean {
    return this.state.isOpen;
  }

  /**
   * Get current modal type
   */
  getCurrentModalType(): ModalType {
    return this.state.type;
  }
}

// Export singleton instance
export const globalModalManager = new GlobalModalManager();

// Export types
export type {
  ModalType,
  ModalData,
  GlobalModalState,
  DeletedProjectModalData,
  ProjectNameMismatchModalData,
  ValidationErrorModalData,
  ExistingProjectWarningModalData,
  ModalListener,
};
